// ConvoCX unified inbox — WebSocket client (CON-845).
// Connects to /ws, authenticates with the JWT, subscribes to the account
// update stream and per-conversation message streams, and routes events to
// handlers for live inbox updates + toast notifications.

import { useEffect, useRef, useCallback } from 'react';
import { getToken } from './api';
import type { Conversation, Message } from './types';

export interface WsEvent {
  type: string;
  message?: Message;
  conversation?: Conversation;
  userId?: string;
}

interface UseInboxSocketOptions {
  token: string | null;
  enabled?: boolean;
  onMessage?: (msg: Message) => void;
  onConversationUpdate?: (conversation: Conversation) => void;
  onAuthenticated?: (userId: string) => void;
}

const MAX_BACKOFF = 30000;
const BASE_DELAY = 1000;

export function useInboxSocket({
  token,
  enabled = true,
  onMessage,
  onConversationUpdate,
  onAuthenticated,
}: UseInboxSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const retryCount = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  const handlersRef = useRef({ onMessage, onConversationUpdate, onAuthenticated });
  handlersRef.current = { onMessage, onConversationUpdate, onAuthenticated };

  const connect = useCallback(() => {
    if (!enabled || !token) return;
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = window.location.host;
    const ws = new WebSocket(`${protocol}://${host}/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      retryCount.current = 0;
      ws.send(JSON.stringify({ type: 'auth', token }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as WsEvent;
        if (data.type === 'authenticated' && data.userId) {
          handlersRef.current.onAuthenticated?.(data.userId);
          return;
        }
        if (data.type === 'new_message' && data.message) {
          handlersRef.current.onMessage?.(data.message);
        }
        if (data.type === 'conversation_updated' && data.conversation) {
          handlersRef.current.onConversationUpdate?.(data.conversation);
        }
      } catch {
        // ignore malformed frames
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (!mounted.current || !enabled) return;
      const delay = Math.min(BASE_DELAY * 2 ** retryCount.current, MAX_BACKOFF);
      retryCount.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token, enabled]);

  useEffect(() => {
    mounted.current = true;
    connect();
    return () => {
      mounted.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  return { connected: wsRef.current?.readyState === WebSocket.OPEN };
}

export function subscribeToConversations(ws: WebSocket | null, conversationIds: string[]) {
  if (!ws || ws.readyState !== WebSocket.OPEN || conversationIds.length === 0) return;
  ws.send(JSON.stringify({ type: 'subscribe', conversationIds }));
}

export function useInboxSocketHelpers() {
  return { getToken };
}